import React, { useState, useRef } from 'react';
import { Upload, File, Folder, Download, X, AlertCircle, CheckCircle, FileText, HardDrive, Calendar, Search, Filter, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadTime: string;
  category: string;
}

const FileTransfer: React.FC = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 过滤文件
  const filteredFiles = files.filter(file => {
    if (searchTerm && !file.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterCategory && file.category !== filterCategory) {
      return false;
    }
    return true;
  });

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 获取文件分类
  const getFileCategory = (type: string): string => {
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type.includes('pdf')) return 'document';
    if (type.includes('text') || type.includes('document')) return 'document';
    if (type.includes('zip') || type.includes('rar') || type.includes('tar')) return 'archive';
    return 'other';
  };

  // 处理文件上传
  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    // 检查文件大小限制 (100MB)
    const maxSize = 100 * 1024 * 1024;
    const invalidFiles = fileArray.filter(file => file.size > maxSize);
    
    if (invalidFiles.length > 0) {
      toast.error(`文件 ${invalidFiles.map(f => f.name).join(', ')} 超过100MB限制`);
      return;
    }

    // 创建文件记录
    const newFiles: UploadedFile[] = fileArray.map(file => ({
      id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadTime: new Date().toLocaleString(),
      category: getFileCategory(file.type)
    }));

    setFiles(prev => [...prev, ...newFiles]);
    toast.success(`成功上传 ${newFiles.length} 个文件`);
  };

  // 删除文件
  const handleDeleteFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    toast.success('文件删除成功');
  };

  // 下载文件
  const handleDownloadFile = (file: UploadedFile) => {
    toast.success(`正在下载 ${file.name}`);
  };

  // 处理拖拽上传
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // 文件卡片组件
  const FileCard: React.FC<{ file: UploadedFile }> = ({ file }) => {
    const getFileIcon = () => {
      switch (file.category) {
        case 'image': return '🖼️';
        case 'video': return '🎥';
        case 'document': return '📄';
        case 'archive': return '📦';
        default: return '📁';
      }
    };

    return (
      <div className="group backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div className="flex items-start justify-between mb-3">
          <div className="text-2xl">{getFileIcon()}</div>
          <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={() => handleDownloadFile(file)}
              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
              title="下载"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteFile(file.id)}
              className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
              title="删除"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-900 truncate mb-1">{file.name}</h4>
          <div className="text-xs text-gray-500 space-y-1">
            <div>{formatFileSize(file.size)}</div>
            <div>{file.uploadTime}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* 页面标题 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-5">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <Upload className="w-7 h-7 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                <HardDrive className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent leading-tight">
                文件传输
              </h1>
              <p className="text-gray-600 mt-2 text-base leading-relaxed">管理您的文件上传和下载</p>
            </div>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="group backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 mb-1">总文件数</p>
                <p className="text-2xl font-bold text-gray-900">{files.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="group backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 mb-1">总大小</p>
                <p className="text-2xl font-bold text-gray-900">{formatFileSize(files.reduce((acc, file) => acc + file.size, 0))}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <HardDrive className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="group backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 mb-1">今日上传</p>
                <p className="text-2xl font-bold text-gray-900">
                  {files.filter(f => f.uploadTime.includes(new Date().toLocaleDateString())).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* 上传区域 */}
        <div 
          className={`backdrop-blur-sm border-2 border-dashed rounded-2xl p-12 text-center shadow-lg transition-all duration-300 ${
            isDragging 
              ? 'border-blue-500 bg-blue-50/80 scale-105' 
              : 'border-gray-300 bg-white/70 hover:border-blue-400 hover:bg-blue-50/50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="max-w-md mx-auto space-y-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all duration-300 ${
              isDragging 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 scale-110' 
                : 'bg-gradient-to-r from-gray-400 to-gray-500'
            }`}>
              <Upload className="w-10 h-10 text-white" />
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isDragging ? '释放文件以上传' : '拖拽文件到此处'}
              </h3>
              <p className="text-gray-600 mb-4">或者点击按钮选择文件</p>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="group flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 mx-auto"
              >
                <FolderOpen className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                <span className="font-medium">选择文件</span>
              </button>
              
              <p className="text-xs text-gray-500 mt-3">支持所有格式，单文件最大100MB</p>
            </div>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
          />
        </div>

        {/* 搜索和筛选 */}
        <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索文件名..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 w-64"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="">全部分类</option>
                  <option value="image">图片</option>
                  <option value="video">视频</option>
                  <option value="document">文档</option>
                  <option value="archive">压缩包</option>
                  <option value="other">其他</option>
                </select>
              </div>
            </div>
            
            <div className="text-sm text-gray-600">
              显示 {filteredFiles.length} / {files.length} 个文件
            </div>
          </div>
        </div>

        {/* 文件列表 */}
        <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg">
          {filteredFiles.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">暂无文件</h3>
              <p className="text-gray-600 mb-4">没有找到符合条件的文件</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 mx-auto"
              >
                <Upload className="w-4 h-4" />
                <span>上传第一个文件</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFiles.map(file => (
                <FileCard key={file.id} file={file} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileTransfer;
